import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Camera } from "lucide-react";
import { scrollToSection } from "@/lib/api";
import { useSiteContent } from "@/lib/content";

export const Hero = ({ onEnquire }) => {
  const c = useSiteContent();
  const LINES = [
    { text: c.hero_line1, cls: "text-slate-50" },
    { text: c.hero_line2, cls: "text-[var(--ekal-accent)]" },
    { text: c.hero_line3, cls: "text-outline" },
  ];
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 220]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.3]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden grain"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          src={c.hero_image}
          alt="Stadium under floodlights"
          className="w-full h-[115%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ekal-bg)] via-black/40 to-transparent" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute top-24 right-4 sm:right-8 lg:right-16 hidden sm:flex flex-col items-end gap-2"
      >
        {c.hero_badges.map((m) => (
          <span
            key={m}
            className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-300 border border-white/15 bg-black/30 backdrop-blur px-3 py-1.5"
          >
            {m}
          </span>
        ))}
      </motion.div>

      <motion.div style={{ opacity }} className="relative px-4 sm:px-8 lg:px-16 pb-16 lg:pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-xs font-mono uppercase tracking-[0.3em] text-amber-400 mb-6 flex items-center gap-3"
        >
          <Camera size={14} /> {c.hero_kicker}
        </motion.p>

        <h1 className="font-heading font-extrabold uppercase leading-[0.9] tracking-tighter text-4xl sm:text-6xl lg:text-7xl max-w-5xl">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.cls}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.16, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-7 max-w-xl text-sm sm:text-base leading-relaxed text-slate-300"
        >
          {c.hero_sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-btn"
            onClick={() => scrollToSection("#coverage")}
            className="group bg-[var(--ekal-accent)] hover:bg-red-600 text-white text-xs font-mono uppercase tracking-[0.2em] px-7 py-4 transition-colors flex items-center gap-2"
          >
            Explore Coverage
            <ArrowDownRight size={15} className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1" />
          </button>
          <button
            data-testid="hero-book-btn"
            onClick={onEnquire}
            className="border border-white/25 hover:border-amber-400 hover:text-amber-400 text-slate-200 text-xs font-mono uppercase tracking-[0.2em] px-7 py-4 transition-colors"
          >
            Book a Shoot
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
